import { lazy } from 'react'
import { ViewerDescriptor } from './types'

// Mirrors EXTENSION_ALIASES in backend/src/core/constants/file_types.py
const EXTENSION_ALIASES: Record<string, string> = {
  jpeg: 'jpg',
}

export const normalizeExtension = (extension?: string | null): string => {
  const normalized = (extension ?? '').trim().toLowerCase().replace(/^\./, '')
  return EXTENSION_ALIASES[normalized] ?? normalized
}

/**
 * Registering a viewer means adding one entry here plus one component under `viewers/`.
 * Anything absent from this list resolves to DownloadOnlyViewer, so a newly uploadable file type
 * degrades to a download rather than breaking the preview dialog.
 */
export const VIEWER_REGISTRY: ViewerDescriptor[] = [
  {
    id: 'pdf',
    extensions: ['pdf'],
    mimeTypes: ['application/pdf'],
    label: 'PDF',
    transport: 'url',
    component: lazy(() => import('./viewers/PdfViewer')),
  },
  {
    id: 'video',
    extensions: ['mp4'],
    mimeTypes: ['video/mp4'],
    label: 'Video',
    transport: 'url',
    component: lazy(() => import('./viewers/VideoViewer')),
  },
  {
    id: 'image',
    // 'jpg' is the canonical extension per FileType.JPEG in the backend; 'png' is FileType.PNG.
    // EXTENSION_ALIASES above maps 'jpeg' → 'jpg' so both .jpeg and .jpg resolve here.
    extensions: ['png', 'jpg'],
    mimeTypes: ['image/png', 'image/jpeg'],
    label: 'Image',
    transport: 'url',
    component: lazy(() => import('./viewers/ImageViewer')),
  },
]

export const findViewer = (extension?: string | null, mimeType?: string | null): ViewerDescriptor | undefined => {
  const normalizedExtension = normalizeExtension(extension)
  if (normalizedExtension) {
    const byExtension = VIEWER_REGISTRY.find((viewer) => viewer.extensions.includes(normalizedExtension))
    if (byExtension) {
      return byExtension
    }
  }

  const normalizedMimeType = mimeType?.trim().toLowerCase()
  if (normalizedMimeType) {
    return VIEWER_REGISTRY.find((viewer) => viewer.mimeTypes.includes(normalizedMimeType))
  }

  return undefined
}

export const hasViewer = (extension?: string | null, mimeType?: string | null): boolean =>
  findViewer(extension, mimeType) !== undefined
